import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Modal, ModalClose, ModalDialog, Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/mtska-frontend-app-component';
import DashboardReportPrintView from './DashboardReportPrintView';
import { countReportDataWidgets } from './flattenReportPages';
import { printDashboardReportElement } from './printDashboardReportElement';
import { scrollPivotTablesToLatest, waitForPivotTablesScrolledToLatest } from './scrollPivotTablesToLatest';
import { useDashboardReportChartsReady } from './useDashboardReportChartsReady';

const DashboardReportPrintModal = ({
    open,
    onClose,
    report,
    title,
    printedAtLabel,
    documentTitle,
    autoPrint = true,
    filterModel,
}) => {
    const { t } = useTranslation();
    const printTargetRef = useRef(null);
    const autoPrintedRef = useRef(false);
    const [printContentMounted, setPrintContentMounted] = useState(open);
    const expectedWidgetCount = useMemo(() => countReportDataWidgets(report), [report]);
    const { ready, timedOut, progress } = useDashboardReportChartsReady(printTargetRef, {
        enabled: open,
        expectedWidgetCount,
    });

    useEffect(() => {
        if (open) {
            setPrintContentMounted(true);
        }
    }, [open]);

    useEffect(() => {
        if (!open || !ready) {
            return;
        }

        const scrollToLatest = () => {
            scrollPivotTablesToLatest(printTargetRef.current);
            return waitForPivotTablesScrolledToLatest(printTargetRef.current);
        };

        scrollToLatest();
    }, [open, ready]);

    useEffect(() => {
        if (!open || !ready || !autoPrint || autoPrintedRef.current) {
            return;
        }

        autoPrintedRef.current = true;
        printDashboardReportElement({
            documentTitle,
        });
    }, [autoPrint, documentTitle, open, ready]);

    const statusLabel = useMemo(() => {
        if (!ready) {
            if (expectedWidgetCount > 0) {
                return `${t('Loading...')} ${progress.loadedWidgets}/${expectedWidgetCount}`;
            }
            return t('Loading...');
        }
        if (timedOut) {
            return t('Some charts may still be loading');
        }
        return t('Ready to print');
    }, [expectedWidgetCount, progress.loadedWidgets, ready, t, timedOut]);

    const handleManualPrint = () => {
        printDashboardReportElement({
            documentTitle,
        });
    };

    return (
        <Modal open={open} onClose={onClose} keepMounted>
            <ModalDialog
                className="mtska-dashboard-print-modal"
                sx={{
                    width: 'min(1280px, 96vw)',
                    maxWidth: '96vw',
                    maxHeight: '96vh',
                    p: 0,
                    overflow: 'hidden',
                }}
            >
                <Box className="mtska-dashboard-print-modal-chrome mtska-no-print">
                    <Box className="mtska-dashboard-print-modal-status">
                        {!ready ? <CircularProgress size="sm" /> : null}
                        <Typography level="title-sm">{statusLabel}</Typography>
                    </Box>
                    <Box className="mtska-dashboard-print-modal-actions">
                        <Button
                            variant="solid"
                            size="sm"
                            icon="faPrint"
                            onClick={handleManualPrint}
                            disabled={!ready}
                        >
                            {t('print')}
                        </Button>
                        <ModalClose />
                    </Box>
                </Box>

                <Box className="mtska-dashboard-print-modal-scroll">
                    {printContentMounted ? (
                        <Box ref={printTargetRef} className="mtska-dashboard-print-target">
                            <DashboardReportPrintView
                                report={report}
                                title={title}
                                printedAtLabel={printedAtLabel}
                                filterModel={filterModel}
                            />
                        </Box>
                    ) : null}
                </Box>
            </ModalDialog>
        </Modal>
    );
};

export default DashboardReportPrintModal;
